import algoliasearch from "algoliasearch";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  type GlobalHit,
  type PersonHit,
  type FacilityHit,
  type SectionHit,
  type ActivityHit,
  createGlobalSearchUiState,
  getGlobalHitListItemContent,
  filterGlobalSearchHits,
  navigateGlobalHit,
  getActivitiesSearchIndexName,
  mapActivityHits,
  GLOBAL_SEARCH_ALL_ACTIVITY_SLOTS
} from "@/components/AlgoliaSearch";
import { collectFilteredHits } from "@/components/AlgoliaSearch/collectFilteredHits";
import AppLayout from "@/layouts/AppLayout";
import PageHeading from "@/components/PageHeading";
import Chip, { ChipSet } from "@/components/Chip";
import Divider from "@/components/Divider";
import EmptyState from "@/components/EmptyState";
import IconButton from "@/components/IconButton";
import ListItem from "@/components/ListItem/ListItem";
import { normalizePhoneSearchQuery } from "@/lib/phoneHelpers";
import usePageTitle from "@/hooks/usePageTitle";
import { useNavigate, Path } from "@/router";

const appId = import.meta.env.VITE_ALGOLIA_APP_ID;
const apiKey = import.meta.env.VITE_ALGOLIA_API_KEY;
const HITS_PER_PAGE = 20;

type ObjectTypeFilter = "all" | "Person" | "Facility" | "Section" | "Activity";

type TypeCounts = { Person: number; Facility: number; Section: number; Activity: number };

const EMPTY_TYPE_COUNTS: TypeCounts = { Person: 0, Facility: 0, Section: 0, Activity: 0 };

function objectTypeEmptyLabel(filter: ObjectTypeFilter): string {
  switch (filter) {
    case "Person":
      return "People";
    case "Facility":
      return "Facilities";
    case "Section":
      return "Sections";
    case "Activity":
      return "Activities";
    default:
      return "Results";
  }
}

function SearchResultCard({
  hit,
  navigate,
  query
}: {
  hit: GlobalHit;
  navigate: ReturnType<typeof useNavigate>;
  query: string;
}) {
  const content = getGlobalHitListItemContent(hit, query, {
    personVisibleAttributes: ["pga_id", "email", "section_name", "facility_name"]
  });
  if (!content) return null;

  switch (hit.object_type) {
    case "Person": {
      const personHit = hit as PersonHit;
      return (
        <ListItem
          compact
          useAvatar
          picture={content.picture}
          headline={content.headline}
          supportingText={content.supportingText}
          tagText="Person"
          tagType="secondary"
          tagStyle="standard"
          tagPosition="trailing"
          longContent
          action={() => {
            const id = personHit.pga_id || personHit.legacy_id;
            if (id) navigate(`/people/${id}` as Path);
          }}
        />
      );
    }
    case "Facility": {
      const facilityHit = hit as FacilityHit;
      return (
        <ListItem
          compact
          useAvatar
          iconName={content.iconName}
          headline={content.headline}
          supportingText={content.supportingText}
          tagText="Facility"
          tagType="tertiary"
          tagStyle="standard"
          tagPosition="trailing"
          longContent={content.longContent}
          action={() => {
            const id = facilityHit.facility_id;
            if (id) navigate(`/facilities/${id}` as Path);
          }}
        />
      );
    }
    case "Section": {
      const sectionHit = hit as SectionHit;
      return (
        <ListItem
          compact
          useAvatar
          iconName={content.iconName}
          headline={content.headline}
          supportingText={content.supportingText}
          tagText="Section"
          tagType="tertiary"
          tagStyle="standard"
          tagPosition="trailing"
          action={() => {
            const id = sectionHit.section_id;
            if (id) navigate(`/sections/${id}` as Path);
          }}
        />
      );
    }
    case "Activity": {
      return (
        <ListItem
          compact
          useAvatar
          iconName={content.iconName}
          overline={content.overline}
          headline={content.headline}
          supportingText={content.supportingText}
          tagText="Activity"
          tagType="tertiary"
          tagStyle="standard"
          tagPosition="trailing"
          longContent={content.longContent}
          action={() => navigateGlobalHit(hit as ActivityHit, navigate)}
        />
      );
    }
    default:
      return null;
  }
}

function algoliaFilters(filter: Exclude<ObjectTypeFilter, "Activity">): string {
  return filter === "all"
    ? "object_type:Person OR object_type:Facility OR object_type:Section"
    : `object_type:${filter}`;
}

export default function GlobalSearchResults() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const navigate = useNavigate();

  const [results, setResults] = useState<GlobalHit[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState<ObjectTypeFilter>("all");
  const [page, setPage] = useState(0);
  const [nbPages, setNbPages] = useState(0);
  const [nbHits, setNbHits] = useState(0);
  const [pageHitCount, setPageHitCount] = useState(0);
  const [typeCounts, setTypeCounts] = useState<TypeCounts>(EMPTY_TYPE_COUNTS);

  usePageTitle("/search", "Search Results");

  const searchClient = useMemo(() => algoliasearch(appId, apiKey), []);
  const index = useMemo(
    () => searchClient.initIndex(import.meta.env.VITE_GLOBAL_PEOPLE_SEARCH_INDEX_NAME),
    [searchClient]
  );
  const activitiesIndexName = getActivitiesSearchIndexName();
  const activitiesIndex = useMemo(
    () => (activitiesIndexName ? searchClient.initIndex(activitiesIndexName) : null),
    [searchClient, activitiesIndexName]
  );

  const baseSearchConfig = useMemo(() => {
    const uiState = createGlobalSearchUiState(import.meta.env.VITE_GLOBAL_PEOPLE_SEARCH_INDEX_NAME);
    const { filters: _filters, ...rest } =
      uiState[import.meta.env.VITE_GLOBAL_PEOPLE_SEARCH_INDEX_NAME]?.configure ?? {};
    return rest;
  }, []);

  useEffect(() => {
    setPage(0);
    setFilter("all");
  }, [query]);

  useEffect(() => {
    if (filter === "Activity" && !activitiesIndex) {
      setFilter("all");
    }
  }, [filter, activitiesIndex]);

  useEffect(() => {
    if (!query.trim()) {
      setTypeCounts(EMPTY_TYPE_COUNTS);
      return;
    }

    const searchQuery = normalizePhoneSearchQuery(query);
    const countParams = { ...baseSearchConfig, hitsPerPage: 0 };

    const activityCount = activitiesIndex
      ? activitiesIndex.search(searchQuery, { hitsPerPage: 0 }).catch((error) => {
          console.error("Algolia activities index search error:", error);
          return { nbHits: 0 };
        })
      : Promise.resolve({ nbHits: 0 });

    Promise.all([
      index.search(searchQuery, { ...countParams, filters: "object_type:Person" }),
      index.search(searchQuery, { ...countParams, filters: "object_type:Facility" }),
      index.search(searchQuery, { ...countParams, filters: "object_type:Section" }),
      activityCount
    ])
      .then(([people, facilities, sections, activities]) => {
        setTypeCounts({
          Person: people.nbHits,
          Facility: facilities.nbHits,
          Section: sections.nbHits,
          Activity: activities.nbHits
        });
      })
      .catch(() => {});
  }, [query, index, activitiesIndex, baseSearchConfig]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setNbPages(0);
      setNbHits(0);
      setPageHitCount(0);
      return;
    }

    const searchQuery = normalizePhoneSearchQuery(query);
    setIsLoading(true);

    const applyActivityOnlyPage = async () => {
      if (!activitiesIndex) {
        return { hits: [] as GlobalHit[], nbHits: 0, nbPages: 0 };
      }

      const result = await activitiesIndex.search(searchQuery, {
        hitsPerPage: HITS_PER_PAGE,
        page
      });
      return {
        hits: mapActivityHits(result.hits as unknown as Array<Record<string, unknown>>),
        nbHits: result.nbHits,
        nbPages: result.nbPages
      };
    };

    const run = async () => {
      if (filter === "Activity") {
        const { hits, nbHits: total, nbPages: pages } = await applyActivityOnlyPage();
        if (hits.length === 0 && page > 0) {
          setPage(page - 1);
          return;
        }
        setResults(hits);
        setPageHitCount(hits.length);
        setNbHits(total);
        setNbPages(pages);
        return;
      }

      const collected = await collectFilteredHits<GlobalHit>(index, searchQuery, {
        searchParams: {
          ...baseSearchConfig,
          filters: algoliaFilters(filter)
        },
        filterHits: filterGlobalSearchHits,
        limit: HITS_PER_PAGE,
        offset: page * HITS_PER_PAGE
      });

      let hits = collected.hits;
      const primaryHitCount = collected.hits.length;

      if (filter === "all" && page === 0 && activitiesIndex) {
        try {
          const activityResult = await activitiesIndex.search(searchQuery, {
            hitsPerPage: GLOBAL_SEARCH_ALL_ACTIVITY_SLOTS
          });
          const activityHits = mapActivityHits(activityResult.hits as unknown as Array<Record<string, unknown>>);
          hits = [...hits, ...activityHits.slice(0, GLOBAL_SEARCH_ALL_ACTIVITY_SLOTS)];
        } catch (error) {
          console.error("Algolia activities index search error:", error);
        }
      }

      if (primaryHitCount === 0 && hits.length === 0 && page > 0) {
        setPage(page - 1);
        return;
      }

      setResults(hits);
      setPageHitCount(primaryHitCount);

      if (primaryHitCount === 0 && hits.length === 0 && !collected.hasMore) {
        const total = collected.exhausted && collected.filteredTotal !== null ? collected.filteredTotal : 0;
        setNbHits(total);
        setNbPages(0);
        return;
      }

      if (collected.exhausted && collected.filteredTotal !== null) {
        setNbHits(collected.filteredTotal);
        setNbPages(collected.filteredTotal === 0 ? 0 : Math.ceil(collected.filteredTotal / HITS_PER_PAGE));
        return;
      }

      const knownThroughPage = page * HITS_PER_PAGE + primaryHitCount;
      setNbHits(collected.hasMore ? knownThroughPage + 1 : knownThroughPage);
      setNbPages(collected.hasMore ? page + 2 : Math.max(page + 1, 1));
    };

    run()
      .catch(() => {
        setResults([]);
        setNbPages(0);
        setNbHits(0);
        setPageHitCount(0);
      })
      .finally(() => setIsLoading(false));
  }, [query, filter, page, index, activitiesIndex, baseSearchConfig]);

  function changeFilter(next: ObjectTypeFilter) {
    setFilter(next);
    setPage(0);
  }

  const allCount = typeCounts.Person + typeCounts.Facility + typeCounts.Section + typeCounts.Activity;
  const selectedTypeCount = filter === "all" ? allCount : typeCounts[filter];
  const totalForFilter = selectedTypeCount > 0 ? selectedTypeCount : nbHits;

  const pageStart = nbHits === 0 || pageHitCount === 0 ? 0 : page * HITS_PER_PAGE + 1;
  const pageEnd = Math.min(pageStart + pageHitCount - 1, nbHits);

  return (
    <AppLayout interior>
      <PageHeading title="Search Results" />

      <p className="mt-2 text-body-large text-sys-brand-on-surface-variant">
        {isLoading
          ? "Searching…"
          : query
            ? `${totalForFilter.toLocaleString()} result${totalForFilter !== 1 ? "s" : ""} for "${query}"`
            : "Enter a search term to see results"}
      </p>

      {(allCount > 0 || isLoading) && (
        <div className="mt-4 mb-6">
          <ChipSet>
            <Chip
              label={`All (${allCount.toLocaleString()})`}
              selected={filter === "all"}
              color="strong"
              showCheckmark={false}
              onClick={() => changeFilter("all")}
            />
            <Chip
              label={`People (${typeCounts.Person.toLocaleString()})`}
              selected={filter === "Person"}
              color="strong"
              showCheckmark={false}
              onClick={() => changeFilter("Person")}
            />
            <Chip
              label={`Facilities (${typeCounts.Facility.toLocaleString()})`}
              selected={filter === "Facility"}
              color="strong"
              showCheckmark={false}
              onClick={() => changeFilter("Facility")}
            />
            <Chip
              label={`Sections (${typeCounts.Section.toLocaleString()})`}
              selected={filter === "Section"}
              color="strong"
              showCheckmark={false}
              onClick={() => changeFilter("Section")}
            />
            {activitiesIndex ? (
              <Chip
                label={`Activities (${typeCounts.Activity.toLocaleString()})`}
                selected={filter === "Activity"}
                color="strong"
                showCheckmark={false}
                onClick={() => changeFilter("Activity")}
              />
            ) : null}
          </ChipSet>
        </div>
      )}

      {isLoading ? (
        <div className="py-8 text-center text-sys-brand-on-surface-variant">Searching…</div>
      ) : results.length === 0 ? (
        <EmptyState
          iconName="search_off"
          title={
            !query
              ? "Enter a Search Term Above"
              : filter !== "all"
                ? `No ${objectTypeEmptyLabel(filter)} Matched "${query}"`
                : `No Results Found for "${query}"`
          }
        />
      ) : (
        <>
          <div>
            {results.map((hit, i) => (
              <div key={`${hit.objectID}-${i}`}>
                {i > 0 && <Divider subtle />}
                <div className="py-3">
                  <SearchResultCard hit={hit} navigate={navigate} query={query} />
                </div>
              </div>
            ))}
          </div>

          {nbPages > 1 && (
            <div className="flex items-center justify-end gap-4 mt-6 pb-4">
              <span className="text-label-large text-sys-brand-on-surface-variant">
                {pageStart}–{pageEnd} of {nbHits.toLocaleString()}
              </span>
              <div className="flex items-center gap-2">
                <IconButton
                  name="navigate_before"
                  variant="square"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page === 0}
                  data-testid="previous-page-button"
                />
                <IconButton
                  name="navigate_next"
                  variant="square"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= nbPages - 1}
                  data-testid="next-page-button"
                />
              </div>
            </div>
          )}
        </>
      )}
    </AppLayout>
  );
}
